import { useEffect } from 'react';

import { Log } from '@/shared/types/analytics';

const PREFETCH_LOOKAHEAD = 3;

interface SessionKeyboardNavOptions {
  fetchMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const useSessionKeyboardNav = (
  data: Log[],
  currentSessionId: string | null | undefined,
  isModalOpen: boolean,
  onNavigate: (session: Log) => void,
  { fetchMore, hasNextPage, isFetchingNextPage }: SessionKeyboardNavOptions = {}
) => {
  useEffect(() => {
    if (!isModalOpen || !hasNextPage || isFetchingNextPage || !fetchMore) {
      return;
    }

    const idx = data.findIndex((s) => s.session_id === currentSessionId);
    // findIndex returns -1 when the open session isn't among the loaded rows. Without this
    // guard -1 satisfies the lookahead check for any short list and prefetches forever.
    if (idx !== -1 && idx >= data.length - PREFETCH_LOOKAHEAD) {
      fetchMore();
    }
  }, [data, currentSessionId, isModalOpen, hasNextPage, isFetchingNextPage, fetchMore]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }
      const target = e.target as HTMLElement;
      if (target.matches('input, textarea, select, [contenteditable]')) {
        return;
      }

      const idx = data.findIndex((s) => s.session_id === currentSessionId);
      if (idx === -1) {
        return;
      }

      e.preventDefault();

      if (e.key === 'ArrowLeft') {
        if (idx > 0) {
          onNavigate(data[idx - 1]);
          window.dispatchEvent(
            new CustomEvent('replay-nav-used', { detail: { direction: 'left' } })
          );
        } else {
          window.dispatchEvent(new CustomEvent('replay-boundary-nudge'));
        }
      } else if (e.key === 'ArrowRight') {
        if (idx < data.length - 1) {
          onNavigate(data[idx + 1]);
          window.dispatchEvent(
            new CustomEvent('replay-nav-used', { detail: { direction: 'right' } })
          );
        } else {
          window.dispatchEvent(new CustomEvent('replay-boundary-nudge'));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data, currentSessionId, isModalOpen, onNavigate]);
};
